import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";

type SourceBrand = {
  id: string;
  displayName: string;
};

type DuplicateBrandFormData = {
  sourceBrandId: string;
  displayName: string;
  iosAppIdentifier?: string | null;
  androidAppIdentifier?: string | null;
};

type Props = {
  sourceBrand: SourceBrand | null;
  isInFlight: boolean;
  onClose: () => void;
  onSubmit: (data: DuplicateBrandFormData) => void;
};

const InternDuplicateBrandDialog = ({
  sourceBrand,
  isInFlight,
  onClose,
  onSubmit,
}: Props) => {
  const [displayName, setDisplayName] = useState("");
  const [iosAppIdentifier, setIosAppIdentifier] = useState("");
  const [androidAppIdentifier, setAndroidAppIdentifier] = useState("");
  const isOpen = sourceBrand !== null;

  useEffect(() => {
    if (!sourceBrand) {
      setDisplayName("");
      setIosAppIdentifier("");
      setAndroidAppIdentifier("");
      return;
    }

    setDisplayName(`${sourceBrand.displayName} Copy`);
    setIosAppIdentifier("");
    setAndroidAppIdentifier("");
  }, [sourceBrand]);

  const handleSubmit = () => {
    if (!sourceBrand || !displayName.trim()) {
      return;
    }

    onSubmit({
      sourceBrandId: sourceBrand.id,
      displayName: displayName.trim(),
      iosAppIdentifier: iosAppIdentifier.trim() || null,
      androidAppIdentifier: androidAppIdentifier.trim() || null,
    });
  };

  return (
    <Dialog open={isOpen} onClose={isInFlight ? undefined : onClose} fullWidth>
      <DialogTitle>Duplicate Brand</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="warning">
            This copies live credentials and integrations except push
            notification credentials. The duplicate will not copy brand admins
            or historical data.
          </Alert>
          <TextField
            label="Source brand"
            value={sourceBrand?.displayName ?? ""}
            disabled
            fullWidth
          />
          <TextField
            label="New brand name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            disabled={isInFlight}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="iOS app identifier"
            value={iosAppIdentifier}
            onChange={(event) => setIosAppIdentifier(event.target.value)}
            disabled={isInFlight}
            fullWidth
            placeholder="Leave blank"
          />
          <TextField
            label="Android app identifier"
            value={androidAppIdentifier}
            onChange={(event) => setAndroidAppIdentifier(event.target.value)}
            disabled={isInFlight}
            fullWidth
            placeholder="Leave blank"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isInFlight}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isInFlight || !displayName.trim()}
          variant="contained"
        >
          Duplicate
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InternDuplicateBrandDialog;
